import {useEffect, useState, useRef} from 'react';
import {useLocation} from 'react-router-dom';
import useUserQuery from '@/hooks/queries/use-user-query';
import useUserPaidGigsCountersQuery from '@/hooks/queries/use-user-paid-gigs-counters';
import {shouldRedirectToOnboarding as checkProfileIncomplete} from '@/utils/onboarding';
import Sentry from '@/services/sentry/Sentry';
import {getEnv} from '@/config/environment';
import type {User} from '@/schema/User';
import type {PaidGigsCounters} from '@/schema/PaidGigsCounters';

const LOADING_TIMEOUT_MS = 2000;

type UseReonboardingResult = {
  isLoading: boolean;
  shouldRedirectToOnboarding: boolean;
};

/**
 * Checks if user has any paid gigs activity that would prevent redirect to onboarding.
 * Users with active business shouldn't be interrupted with onboarding.
 */
function hasPaidGigsActivity(counters: PaidGigsCounters | undefined): boolean {
  if (!counters) return false;
  const {invites, pending, active} = counters;
  return invites > 0 || pending > 0 || active > 0;
}

/**
 * Hook to determine if user should be redirected to onboarding.
 *
 * Redirect happens when ALL of these are true:
 * 1. VITE_ENABLE_REONBOARDING environment variable is set to 'true'
 * 2. User is not already on onboarding page
 * 3. User has incomplete profile (missing required fields)
 * 4. User has no paid gigs activity (invites, pending, or active)
 *
 * Has a 2-second timeout to prevent blocking the UI if queries are slow.
 * Fetches fresh data on mount and uses it for the rest of the session.
 */
function useReonboarding(): UseReonboardingResult {
  const location = useLocation();
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const [sessionUser, setSessionUser] = useState<User | undefined>(undefined);
  const [sessionPaidGigsCounters, setSessionPaidGigsCounters] = useState<
    PaidGigsCounters | undefined
  >(undefined);
  const [isFetchingFreshData, setIsFetchingFreshData] = useState(true);
  const hasFetchedFreshData = useRef(false);

  const {user: queryUser, refetch: refetchUser} = useUserQuery<User>({
    include: ['profile.profile_progress_report'],
  });

  const {refetch: refetchPaidGigsCounters} = useUserPaidGigsCountersQuery({
    enabled: !!queryUser,
  });

  // Fetch fresh data on mount and store it for the session
  useEffect(() => {
    if (hasFetchedFreshData.current) return;

    const fetchFreshData = async () => {
      hasFetchedFreshData.current = true;

      // Fetch both queries in parallel to get fresh data
      // Use Promise.allSettled to handle partial failures gracefully
      const results = await Promise.allSettled([
        refetchUser(),
        refetchPaidGigsCounters(),
      ]);

      if (results[0].status === 'fulfilled') {
        setSessionUser(results[0].value.data?.data);
      } else {
        Sentry.addBreadcrumb({
          type: 'error',
          category: 'reonboarding',
          message: 'Failed to fetch fresh user data for reonboarding check',
          level: 'error',
          data: {
            error: results[0].reason?.message || String(results[0].reason),
          },
        });
        setSessionUser(undefined);
      }

      if (results[1].status === 'fulfilled') {
        setSessionPaidGigsCounters(results[1].value.data);
      } else {
        Sentry.addBreadcrumb({
          type: 'error',
          category: 'reonboarding',
          message:
            'Failed to fetch fresh paid gigs counters for reonboarding check',
          level: 'error',
          data: {
            error: results[1].reason?.message || String(results[1].reason),
          },
        });
        setSessionPaidGigsCounters(undefined);
      }

      setIsFetchingFreshData(false);
    };

    // Start fetching fresh data immediately on mount
    fetchFreshData();
  }, [refetchUser, refetchPaidGigsCounters]);

  // Consider loading until we have attempted to fetch fresh data
  // Once fetch completes (success or failure), stop loading
  const isLoading = isFetchingFreshData;

  // Timeout to prevent indefinite loading state
  useEffect(() => {
    if (!isLoading || hasTimedOut) return undefined;

    const timeoutId = setTimeout(() => {
      setHasTimedOut(true);
    }, LOADING_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, [isLoading, hasTimedOut]);

  // Once timed out, NEVER redirect for the rest of this session.
  // This prevents jarring redirects after the app has already loaded.
  // Even if data arrives later showing user needs onboarding, we don't redirect.
  if (hasTimedOut) {
    return {isLoading: false, shouldRedirectToOnboarding: false};
  }

  // Still loading, wait for data
  if (isLoading) {
    return {isLoading: true, shouldRedirectToOnboarding: false};
  }

  // Data loaded - determine if we should redirect
  // If we don't have user data, don't redirect (safer to not interrupt user)
  if (!sessionUser) {
    return {isLoading: false, shouldRedirectToOnboarding: false};
  }

  const isOnOnboardingPage = location.pathname.startsWith('/onboarding');
  const hasIncompleteProfile = checkProfileIncomplete(sessionUser);
  const hasBusinessActivity = hasPaidGigsActivity(sessionPaidGigsCounters);
  const isReonboardingEnabled = getEnv().VITE_ENABLE_REONBOARDING === 'true';

  const shouldRedirectToOnboarding =
    isReonboardingEnabled &&
    !isOnOnboardingPage &&
    hasIncompleteProfile &&
    !hasBusinessActivity;

  return {isLoading: false, shouldRedirectToOnboarding};
}

export default useReonboarding;
